package com.fireavert.menu.client.presentation

import com.fireavert.cache.client.Preferences
import com.fireavert.common.Try
import com.fireavert.logging.Logger
import com.fireavert.properties.shared.client.ClientPropertyRepository
import com.fireavert.properties.shared.client.PropertyNavigator
import com.fireavert.user.data_models.Permission
import com.fireavert.user.data_models.RolesModel
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

class MenuInteractor(
    private val logger: Logger,
    private val screen: MenuScreen,
    private val preferences: Preferences,
    private val menuNotificationRepository: MenuNotificationRepository,
    private val propertyRepository: ClientPropertyRepository,
    private val globalFilterController: GlobalFilterController,
    private val propertyNavigator: PropertyNavigator,
) {
    fun didLoad() {
        screen.showAdministrationMenuItem = preferences.roles.any { it.hasAtLeastLevelOf(RolesModel.PROPERTY_ADMIN) }
        screen.setShowClientsMenuItem(preferences.permissions.contains(Permission.ReadClients))
        screen.setShowUsersMenuItem(preferences.permissions.contains(Permission.ReadUsers))
        screen.scope.launch {
            when (val dashboard = menuNotificationRepository.getDashboardNotification()) {
                is Try.Success -> {
                    screen.setDashboardNumber(dashboard.value)
                }
                is Try.Error -> {
                    screen.setDashboardNumber(0)
                }
            }
            when (val property = menuNotificationRepository.getPropertiesNotification()) {
                is Try.Success -> {
                    screen.setPropertiesNumber(property.value)
                }
                is Try.Error -> {
                    screen.setPropertiesNumber(0)
                }
            }

            when (val properties = propertyRepository.getPropertiesNameAndIdMap()) {
                is Try.Success -> {
                    screen.setPropertyMap(properties.value)
                }
                is Try.Error -> {
                    screen.setPropertyMap(emptyMap())
                }
            }

        }
    }

    suspend fun updatePropertyPreferences(propertyId: Int?, pathname: String) {
        preferences.globalProperty = propertyId
        screen.setGlobalProperty(propertyId)  // Update the ViewModel
        refreshPages(pathname)
    }

    suspend fun updateDateRangePreferences(startDate: LocalDate?, endDate: LocalDate?, pathName: String) {
        preferences.globalStartDate = startDate
        preferences.globalEndDate = endDate
        screen.setGlobalDateRange(startDate, endDate)
        refreshPages(pathName)
    }

    suspend fun refreshPages(pathName: String) {
        // Use regex to check if the pathName is exactly "properties/{number}" with no additional segments
        val propertyDetailPattern = """^/app/properties/(\d+)$""".toRegex()
        val matchResult = propertyDetailPattern.find(pathName)

        if (pathName.contains("properties")) {
            // Always disable time selector for any properties page
            screen.setDisableTimeSelector(true)

            // Only redirect if we're on the exact property detail page (not a sub-page)
            if (matchResult != null) {
                // We are on a property detail page, so check if global property is set
                if (preferences.globalProperty == null || preferences.globalProperty == 0) {
                    propertyNavigator.navigate(PropertyNavigator.Destination.Properties)
                    return
                } else {
                    propertyNavigator.navigate(PropertyNavigator.Destination.Property(preferences.globalProperty!!))
                    return
                }
            }
            // Otherwise, we're on a sub-page like units or edit, so don't redirect
            return
        }

        val suffixSplit = pathName.split("/").last()
        when (suffixSplit) {
            "dashboard" -> {
                screen.setDisableTimeSelector(false)
                globalFilterController.loadDashboardData(preferences.globalStartDate ?: LocalDate.fromEpochDays(1),
                    preferences.globalEndDate ?: LocalDate.fromEpochDays(2),
                    preferences.globalProperty)
            }

            "reports" -> {
                screen.setDisableTimeSelector(true)
                globalFilterController.loadReportsData(preferences.globalProperty)
            }
            "my_fire_risk" -> {
                screen.setDisableTimeSelector(true)
                globalFilterController.loadFireRiskData()
            }
        }
    }
}